import { useModalContext } from 'context/EventModalContext'
import { useCallback } from 'react'
import BaseModal from './BaseModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import Button from 'components/Button'
import { isPresentAndTruthy } from 'lib/helpers'

interface IOption {
  label: string
  onClick: Callback
  icon?: IconProp
  info?: string
}

interface IProps {
  title: string
  body: string
  cancelLabel?: string
  onCancel?: Callback
  options: (IOption | false | null)[]
}

export function useOptionsModal() {
  const { setModalContent } = useModalContext()
  return useCallback(
    (props: IProps) => {
      setModalContent(<OptionsModal {...props} />)
    },
    [setModalContent]
  )
}

export function OptionsModal({
  title,
  body,
  cancelLabel = 'Close',
  onCancel: cancelCallback,
  options
}: IProps) {
  const { hideModal } = useModalContext()
  const onCancel = () => {
    cancelCallback?.()
    hideModal()
  }

  return (
    <BaseModal>
      <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
        <div className="sm:flex sm:items-start">
          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <h3
              className="text-lg leading-6 font-medium text-gray-900"
              id="modal-headline"
            >
              {title}
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500">{body}</p>
            </div>
          </div>
        </div>

        <div className="space-y-2 mt-4">
          {options
            .filter(x => x !== false)
            .filter(isPresentAndTruthy)
            .map(({ label, info, icon, onClick }, index) => (
              <div
                key={`option-${index}`}
                onClick={onClick}
                className="cursor-pointer flex items-center space-x-1 hover:bg-gray-100 py-2 px-2 rounded-lg"
              >
                {!!icon && (
                  <FontAwesomeIcon
                    icon={icon}
                    size={'lg'}
                    fixedWidth
                    className={'text-gray-500'}
                  />
                )}
                <div>
                  <p className="text-sm text-gray-700">{label}</p>
                  {!!info && <p className="text-sm text-gray-400">{info}</p>}
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="bg-gray-50 py-3 px-6 flex flex-row-reverse">
        <Button
          onClick={onCancel}
          color={'white'}
          label={cancelLabel}
          className={'mr-3'}
        />
      </div>
    </BaseModal>
  )
}
